import { useForm as useLocalForm } from 'react-hook-form'
import { useFormContext, useWatch } from 'react-hook-form'
import { useState, useEffect } from 'react'

interface Option {
  id: string
  label: string
}

interface PaymentItem {
  label: string
  erpStatus: string
  paymentId: string
}

interface LocalFormValues {
  label: string
  erpStatus: string
}

const PosPaymentMapper = () => {
  const { setValue, getValues } = useFormContext()
  const {
    register,
    reset,
    watch,
    setError,
    formState: { errors },
  } = useLocalForm<LocalFormValues>({
    defaultValues: { label: '', erpStatus: '' },
  })

  const [isAddingPayment, setIsAddingPayment] = useState(false)
  const label = watch('label')
  const erpStatus = watch('erpStatus')
  const paymentMethods = useWatch({ name: 'mobile.payments' }) || []

  // Ensure payments are initialized as an empty array when the component mounts
  useEffect(() => {
    // Initialize payments as an empty array if it's undefined
    if (!getValues('mobile.payments')) {
      setValue('mobile.payments', [])
    }
  }, [getValues, setValue])

  const predefinedOptions: Option[] = [
    { id: 'COD', label: 'COD' },
    { id: 'CCOD', label: 'CCOD' },
    { id: 'CREDITSALE', label: 'Points' },
    { id: 'PREPAID', label: 'Prepaid' },
  ]

  const savePayment = (paymentId: string) => {
    if (!label || !erpStatus) return

    const currentPayments = getValues('mobile.payments') || []

    // Check for duplicate label (case-insensitive)
    const isDuplicateLabel = currentPayments.some(
      (payment: PaymentItem) =>
        payment.label.toLowerCase() === label.toLowerCase(),
    )

    if (isDuplicateLabel) {
      setError('label', {
        type: 'manual',
        message:
          'A payment method with this label already exists. Please use a different label.',
      })
      return
    }

    const updatedItem: PaymentItem = {
      label,
      erpStatus,
      paymentId,
    }

    // Always add as new entry since only labels need to be unique
    currentPayments.push(updatedItem)

    setValue('mobile.payments', [...currentPayments])
    reset()
    setIsAddingPayment(false)
  }

  const handleRemove = (paymentId: string) => {
    const currentPayments = getValues('mobile.payments') || []
    const updatedPayments = currentPayments.filter(
      (payment: PaymentItem) => payment.paymentId !== paymentId,
    )

    // Always set the payments array in the form state, even if it's empty
    setValue('mobile.payments', updatedPayments)
  }

  return (
    <div className="mt-6 rounded-lg border border-gray-300 bg-gray-50 p-4">
      <h3 className="mb-4 font-semibold">Payment Configuration</h3>

      {isAddingPayment ? (
        // Payment form
        <div className="space-y-4">
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="w-full md:w-1/2">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Payment Label
              </label>
              <input
                type="text"
                placeholder="Enter payment label"
                {...register('label')}
                className={`w-full rounded-lg border p-2.5 text-sm focus:outline-none ${
                  errors.label
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-blue-500'
                }`}
              />
              {errors.label && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.label.message}
                </p>
              )}
            </div>

            <div className="w-full md:w-1/2">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                ERP Status
              </label>
              <input
                type="text"
                placeholder="Enter ERP status"
                {...register('erpStatus')}
                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {label && erpStatus && (
            <div className="mt-4 flex flex-wrap gap-2">
              {predefinedOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="rounded-lg bg-black px-4 py-2 text-sm text-white transition-opacity hover:opacity-90"
                  onClick={() => savePayment(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="rounded-lg bg-gray-300 px-4 py-2 text-sm text-gray-800 hover:bg-gray-400"
              onClick={() => {
                reset()
                setIsAddingPayment(false)
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        // Add Payment Method button
        <button
          type="button"
          className="rounded-lg bg-black px-4 py-2 text-sm text-white transition-opacity hover:opacity-90"
          onClick={() => setIsAddingPayment(true)}
        >
          Add Payment Method
        </button>
      )}

      {paymentMethods && paymentMethods.length > 0 ? (
        <div className="mt-6">
          <h4 className="mb-2 text-sm font-medium">
            Configured Payment Methods
          </h4>
          <div className="space-y-2">
            {paymentMethods.map((payment: PaymentItem) => (
              <div
                key={payment.paymentId}
                className="flex items-center justify-between rounded-lg border border-gray-300 bg-white p-3"
              >
                <div className="flex flex-col">
                  <span className="font-medium">
                    {payment.label} ({payment.paymentId})
                  </span>
                  <span className="text-xs text-gray-500">
                    ERP Status: {payment.erpStatus}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded border border-red-400 bg-white px-3 py-1 text-sm text-red-600 hover:bg-red-50"
                    onClick={() => handleRemove(payment.paymentId)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default PosPaymentMapper
